import { FormInstance, message } from 'antd';
import axios from 'axios';
import { ApiService } from 'service/api/apiService';
import { seasonApiService } from 'service/api/seasonApiService';
import { SeasonInterface, SeasonData, SeasonApiRequest } from 'type/seasonType';

class SeasonService {
  constructor(private apiService: ApiService<SeasonInterface>) {}

  public async creatSeason(form: FormInstance): Promise<SeasonInterface | null> {
    try {
      await form.validateFields();
      const newSeason: SeasonInterface = {
        id: -1,
        title: form.getFieldValue('title'),
        slug: form.getFieldValue('slug'),
        ...(form.getFieldValue('synopsis') && { synopsis: form.getFieldValue('synopsis') }),
        synopsis_en: form.getFieldValue('synopsis_en'),
        title_en: form.getFieldValue('title_en'),
        status: form.getFieldValue('status'),
        dubbing_mode: form.getFieldValue('dubbing_mode'),

        ...(form.getFieldValue('season_order') && {
          season_order: form.getFieldValue('season_order'),
        }),
        age_rating: form.getFieldValue('age_rating'),
        ...(form.getFieldValue('release_date_start') && {
          release_date_start: form.getFieldValue('release_date_start').format('YYYY-MM-DD'),
        }),

        ...(form.getFieldValue('release_date_end') && {
          release_date_end: form.getFieldValue('release_date_end').format('YYYY-MM-DD'),
        }),
        ...(form.getFieldValue('produce_country') && {
          produce_country: form.getFieldValue('produce_country'),
        }),
        ...(form.getFieldValue('cast') && { cast: form.getFieldValue('cast') }),
        ...(form.getFieldValue('genres') && { genres: form.getFieldValue('genres') }),
        categories: [],
      };
      const createdSeason = await this.apiService.create(newSeason);
      return createdSeason;
    } catch {
      return null;
    }
  }

  public async updateSeason(id: number, form: FormInstance): Promise<SeasonInterface | null> {
    try {
      await form.validateFields();
      const newSeasons: SeasonInterface = {
        id: Number(id),
        title: form.getFieldValue('title'),
        title_en: form.getFieldValue('title_en'),
        status: form.getFieldValue('status'),
        dubbing_mode: form.getFieldValue('dubbing_mode'),
        slug: form.getFieldValue('slug'),
        age_rating: form.getFieldValue('age_rating'),
        ...(form.getFieldValue('synopsis') && { synopsis: form.getFieldValue('synopsis') }),
        synopsis_en: form.getFieldValue('synopsis_en'),
        ...(form.getFieldValue('season_order') && {
          season_order: form.getFieldValue('season_order'),
        }),
        ...(form.getFieldValue('release_date_start') && {
          release_date_start: form.getFieldValue('release_date_start').format('YYYY-MM-DD'),
        }),
        ...(form.getFieldValue('release_date_end') && {
          release_date_end: form.getFieldValue('release_date_end').format('YYYY-MM-DD'),
        }),
        ...(form.getFieldValue('produce_country') && {
          produce_country: form.getFieldValue('produce_country'),
        }),
        ...(form.getFieldValue('cast') && { cast: form.getFieldValue('cast') }),
        ...(form.getFieldValue('genres') && { genres: form.getFieldValue('genres') }),
        categories: [],
      };

      return await this.apiService.update(Number(id), newSeasons);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        message.error('Failed to update : ' + JSON.stringify(error.response.data));
      }
      return null;
    }
  }

  /**
   * i18n 필드를 포함한 Season 조회
   */
  public async fetchSeasonWithI18n(id: number): Promise<SeasonData> {
    return await seasonApiService.fetchSeasonWithI18n(id);
  }

  /**
   * i18n 필드를 포함한 Season 업데이트
   */
  public async updateSeasonWithI18n(id: number, seasonData: SeasonApiRequest): Promise<SeasonData> {
    return await seasonApiService.updateSeasonWithI18n(id, seasonData);
  }

  /**
   * i18n 필드를 포함한 Season 생성
   */
  public async createSeasonWithI18n(seasonData: SeasonApiRequest): Promise<SeasonData> {
    return await seasonApiService.createSeasonWithI18n(seasonData);
  }

  /**
   * i18n 필드를 포함한 Season 목록 조회
   */
  public async fetchSeasonsWithI18n(params?: URLSearchParams): Promise<any> {
    return await seasonApiService.fetchSeasonListWithI18n(params);
  }

  /**
   * 특정 Series에 속한 Season 목록 조회 (i18n 지원)
   */
  public async fetchSeasonsBySeriesWithI18n(
    seriesId: number,
    params?: URLSearchParams
  ): Promise<any> {
    return await seasonApiService.fetchSeasonsBySeriesWithI18n(seriesId, params);
  }

  /**
   * 언어별 Season 조회
   */
  public async fetchSeasonsByLanguage(language: string, params?: URLSearchParams): Promise<any> {
    return await seasonApiService.fetchSeasonsByLanguage(language, params);
  }

  /**
   * 번역 상태별 Season 조회
   */
  public async fetchSeasonsByTranslationStatus(
    requiredLanguages: string[],
    params?: URLSearchParams
  ): Promise<any> {
    return await seasonApiService.fetchSeasonsByTranslationStatus(requiredLanguages, params);
  }
}

export const seasonService = new SeasonService(seasonApiService);
